import React, {useEffect, forwardRef} from 'react'
import styled from 'styled-components'
import {Box, BoxProps, useRefObjectAsForwardedRef, sx, SxProp} from '../..'

import {useElementCenterY} from './hooks/useElementCenterY'
import {useCenterY} from './context/VerticalPositionContext'
import {PositionAwareItemProps} from './types'
/**
 *
 * A component that wraps its children and calculates the vertical center
 * position of its boundingClientRect using the useElementCenterY hook.
 *
 * Tracks its own centerY value and informs the parent when it is the top-most child
 * using onTopChildCenterYChange.
 *
 * Manages its selected state and updates the parent's select index using onItemClick.
 *
 * Any child components of PositionAwareItem that use the forwardedRef
 * will receive the correct ref to access the underlying DOM element.
 * This allows you to access the DOM element and call methods or
 * access properties directly on the child components.
 */

const PositionAwareItem = forwardRef<HTMLDivElement, PositionAwareItemProps>(
  (
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    {
      id = '',
      index,
      children,
      trackCenterY,
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      onTopChildCenterYChange = () => {},
      onItemSelect,
      sx: sxProp,
      testId,
      isSelected: isSelectedProp,
      ...props
    },
    forwardedRef
  ) => {
    const wrapperTestId = `${testId}-wrapper`

    // Custom hook to get the centerY value and a ref for the DOM element
    const {centerY, elementRef} = useElementCenterY()
    const {
      centerY: contextCenterY,
      setCenterY: setContextCenterY,
      topChildCenterY,
      setTopChildCenterY,
      selectedItemId,
      setSelectedItemId
    } = useCenterY()

    const isSelected = isSelectedProp !== undefined ? isSelectedProp : selectedItemId === id

    // State to manage the selected state locally
    // const [localIsSelected, setLocalIsSelected] = useState(isSelected || false)

    useEffect(() => {
      // When the component mounts, call the onTopChildCenterYChange callback if it's the top-most child
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (trackCenterY && onTopChildCenterYChange) {
        onTopChildCenterYChange(centerY ?? 0)
      }
    }, [centerY, onTopChildCenterYChange, trackCenterY])

    // Define the type for the ref object of PositionAwareItem
    type PositionAwareItemRef = HTMLDivElement | null

    // Use the custom hook to synchronize the ref between local ref and forwarded ref
    useRefObjectAsForwardedRef(forwardedRef, elementRef as React.MutableRefObject<PositionAwareItemRef>)

    const handleClick = () => {
      if (!isSelected) {
        // Not selected, so we select this item
        setSelectedItemId(id) // Use the id
        setContextCenterY(centerY ?? 0)
        setTopChildCenterY(topChildCenterY ?? 0)
        if (onItemSelect) {
          onItemSelect(centerY ?? 0)
        }
        // notify the parent if this position aware item is the top-most child within
        // used to tell a Step to update its selected visual style
        if (trackCenterY) {
          if (centerY !== null) {
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            onTopChildCenterYChange(centerY ?? 0)
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            elementRef.current!.dispatchEvent(new CustomEvent('childCenterYChange', {detail: centerY}))
          }
        }
      } else {
        // Already selected, so we deselect this item
        setSelectedItemId(null)
        setContextCenterY(0)
        setTopChildCenterY(0)
      }
    }
    // derive center-y value, set as a string in a data-attribute
    // Render the component with the appropriate styles based on the selected state
    return (
      <StyledPositionalItemWrapper
        ref={elementRef as React.RefObject<HTMLDivElement>}
        onClick={handleClick}
        isSelected={isSelected}
        sx={sxProp}
        data-testid={testId ? wrapperTestId : null}
        data-center-y={centerY}
      >
        {children && Array.isArray(children)
          ? React.Children.map(children, child =>
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              React.isValidElement(child) ? React.cloneElement(child, {isSelected} as any) : child
            )
          : React.isValidElement(children)
          ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
            React.cloneElement(children, {isSelected} as any)
          : children}
      </StyledPositionalItemWrapper>
    )
  }
)

const StyledPositionalItemWrapper = styled(Box)<BoxProps & PositionAwareItemProps>`
  ${sx}
`

PositionAwareItem.displayName = 'PositionAwareItem'
export default PositionAwareItem
